import clsx from 'clsx';
import { Download, Pause, Play, RefreshCw, Search, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchJSON } from '../api';
import { apiPath, FLOW } from '../constants';
import { useCsvExport } from '../csvExport';
import { showToast } from '../toast';
import { CsvExportProgress } from './CsvExportProgress';

type BatchPreview = {
  eligible_total: number;
  selected: number;
  sampled: number;
  valid: number;
  already_cached: number;
  invalid_preview: number;
};

type BatchSummary = {
  batch_id: string;
  status: 'running' | 'paused' | 'draining' | 'completed' | 'completed_with_errors';
  requested_limit: number;
  group_size: number;
  total_selected: number;
  created_by: string;
  created_at_ms: number;
  updated_at_ms: number;
  completed_at_ms: number | null;
  counts: Record<string, number>;
  retry_waiting: number;
  continuous: boolean;
  measured_bytes: number;
  submitted_bytes: number;
  last_run_submitted_count: number;
  last_run_submitted_bytes: number;
  last_run_estimated_mbps: number;
};

type BatchTask = {
  episode_id: string;
  batch_position: number;
  status: string;
  attempt_count: number;
  last_error: string | null;
};

type PrefetchOverview = {
  current_cache_valid: number;
  ever_confirmed_warm: number;
  edgeone_accepted: number;
  remaining_eligible: number;
};

type BatchStatusResponse = {
  batch: BatchSummary | null;
  tasks: BatchTask[];
  overview: PrefetchOverview;
};

const TERMINAL_STATUSES = ['success', 'already_cached', 'failed', 'timeout', 'invalid', 'skipped'];

function statusLabel(status: string): string {
  return status.split('_').join(' ');
}

function formatBytes(bytes: number): string {
  return `${(Math.max(0, bytes) / 1024 ** 3).toFixed(2)} GB`;
}

function formatBandwidth(mbps: number): string {
  return mbps >= 1000 ? `${(mbps / 1000).toFixed(2)} Gbps` : `${mbps.toFixed(1)} Mbps`;
}

export function EdgeOnePrefetchAdmin() {
  const [limit, setLimit] = useState(500);
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [batch, setBatch] = useState<BatchSummary | null>(null);
  const [tasks, setTasks] = useState<BatchTask[]>([]);
  const [overview, setOverview] = useState<PrefetchOverview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'start' | 'action' | null>(null);
  const { exportState, isExporting, downloadCsv } = useCsvExport();

  const loadStatus = useCallback(async () => {
    const response = await fetchJSON<BatchStatusResponse>(
      apiPath('/admin/edgeone-prefetch/batch/status?limit=100')
    );
    setBatch(response?.batch ?? null);
    setTasks(response?.tasks ?? []);
    setOverview(response?.overview ?? null);
  }, []);

  useEffect(() => {
    void loadStatus().catch(() => showToast('批量预热状态加载失败'));
  }, [loadStatus]);

  useEffect(() => {
    if (!batch || (!['running', 'paused'].includes(batch.status) && !batch.continuous)) return;
    const timer = window.setInterval(() => {
      void loadStatus().catch(() => undefined);
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [batch, loadStatus]);

  const terminalCount = useMemo(
    () => TERMINAL_STATUSES.reduce((total, status) => total + (batch?.counts[status] ?? 0), 0),
    [batch]
  );
  const progress = batch?.total_selected
    ? Math.min(100, Math.round((terminalCount / batch.total_selected) * 100))
    : 0;
  const active = batch != null && ['running', 'paused'].includes(batch.status);
  const controlled = active || batch?.continuous === true;

  const runPreview = async () => {
    setBusy('preview');
    setPreview(null);
    try {
      const result = await fetchJSON<BatchPreview>(
        apiPath('/admin/edgeone-prefetch/batch/preview'),
        { method: 'post', json: { limit }, timeout: 120_000 }
      );
      setPreview(result);
      showToast('预热候选检查完成');
    } catch {
      showToast('预热候选检查失败');
    } finally {
      setBusy(null);
    }
  };

  const startBatch = async () => {
    setBusy('start');
    try {
      const result = await fetchJSON<{ created: boolean; error?: string }>(
        apiPath('/admin/edgeone-prefetch/batch/start'),
        { method: 'post', json: { limit, continuous: true } }
      );
      if (!result?.created) throw new Error(result?.error ?? 'Batch was not created');
      setPreview(null);
      await loadStatus();
      showToast('批量预热已开始');
    } catch {
      showToast('批量预热启动失败');
    } finally {
      setBusy(null);
    }
  };

  const changeBatchState = async (action: 'pause' | 'resume' | 'stop') => {
    if (!batch) return;
    setBusy('action');
    try {
      await fetchJSON(apiPath('/admin/edgeone-prefetch/batch/action'), {
        method: 'post',
        json: { batch_id: batch.batch_id, action },
      });
      await loadStatus();
      showToast(
        action === 'pause'
          ? '批量预热已暂停'
          : action === 'resume'
            ? '批量预热已恢复'
            : '持续预热将在当前批次后停止'
      );
    } catch {
      showToast(action === 'pause' ? '暂停失败' : action === 'resume' ? '恢复失败' : '停止失败');
    } finally {
      setBusy(null);
    }
  };

  const exportCsv = async () => {
    if (!batch) return;
    try {
      await downloadCsv({
        path: `/admin/edgeone-prefetch/batch/csv?batch_id=${encodeURIComponent(batch.batch_id)}`,
        filename: `${FLOW}-edgeone-prefetch-${batch.batch_id}.csv`,
        label: 'EdgeOne batch prefetch details',
      });
      showToast('批量预热 CSV 已导出');
    } catch {
      showToast('批量预热 CSV 导出失败');
    }
  };

  return (
    <section
      className="space-y-4 border-y border-brand-border py-6"
      aria-label="Annotation queue preview prefetch"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-brand-text-primary">
            Annotation Queue Preview Prefetch
          </h2>
          <p className="mt-1 text-xs text-brand-text-secondary">
            Continuous {FLOW === 'secshot' ? 'SecShot' : 'SecCap'} Voting and Pending prefetch · up
            to 500 episodes per minute
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-brand-text-secondary">
            <span>Episodes</span>
            <select
              aria-label="Prefetch episode limit"
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPreview(null);
              }}
              disabled={controlled || busy != null}
              className="h-9 rounded-md border border-brand-border bg-brand-dark px-2 text-brand-text-primary focus:border-brand-orange focus:outline-none disabled:opacity-50"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={controlled || busy != null}
            className="flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs font-medium text-brand-text-secondary transition hover:text-white disabled:opacity-50"
          >
            <Search className={clsx('h-4 w-4', busy === 'preview' && 'animate-pulse')} />
            Dry Run
          </button>
          <button
            type="button"
            onClick={() => void startBatch()}
            disabled={controlled || busy != null || !preview || preview.selected === 0}
            className="flex h-9 items-center gap-2 rounded-md bg-brand-orange px-3 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            <Play className="h-4 w-4" />
            Start
          </button>
          {batch?.status === 'running' && (
            <button
              type="button"
              onClick={() => void changeBatchState('pause')}
              disabled={busy != null}
              className="flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs font-medium text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            >
              <Pause className="h-4 w-4" /> Pause
            </button>
          )}
          {batch?.status === 'paused' && (
            <button
              type="button"
              onClick={() => void changeBatchState('resume')}
              disabled={busy != null}
              className="flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs font-medium text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            >
              <Play className="h-4 w-4" /> Resume
            </button>
          )}
          {batch?.continuous && (
            <button
              type="button"
              onClick={() => void changeBatchState('stop')}
              disabled={busy != null}
              className="flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs font-medium text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            >
              <Square className="h-4 w-4" /> Stop continuous
            </button>
          )}
          <button
            type="button"
            onClick={() => void loadStatus()}
            disabled={busy != null}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary transition hover:text-white disabled:opacity-50"
            title="Refresh status"
            aria-label="Refresh prefetch status"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => void exportCsv()}
            disabled={!batch || isExporting}
            className="flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-xs font-medium text-brand-text-secondary transition hover:text-white disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      {overview && (
        <div
          className="grid gap-3 text-xs sm:grid-cols-2 xl:grid-cols-4"
          data-testid="prefetch-overview"
        >
          {[
            ['Current Cache Valid', overview.current_cache_valid],
            ['Ever Confirmed Warm', overview.ever_confirmed_warm],
            ['EdgeOne Accepted', overview.edgeone_accepted],
            ['Remaining Eligible', overview.remaining_eligible],
          ].map(([label, value]) => (
            <div key={label} className="border-l-2 border-brand-orange px-3 py-1">
              <div className="text-brand-text-secondary">{label}</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {Number(value).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="grid gap-3 text-xs sm:grid-cols-5" data-testid="prefetch-preview">
          {[
            ['Selected', preview.selected],
            ['Sampled', preview.sampled],
            ['Sample ready', preview.valid],
            ['Sample cached', preview.already_cached],
            ['Sample invalid', preview.invalid_preview],
          ].map(([label, value]) => (
            <div key={label} className="border-l-2 border-brand-orange px-3 py-1">
              <div className="text-brand-text-secondary">{label}</div>
              <div className="mt-1 font-mono text-lg font-semibold text-brand-text-primary">
                {Number(value).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {batch && (
        <div className="space-y-3" data-testid="prefetch-batch-progress">
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="font-medium capitalize text-brand-text-primary">
              {statusLabel(batch.status)}
              {batch.continuous ? ' · continuous' : ''}
            </span>
            <span className="font-mono text-brand-text-secondary">
              {terminalCount.toLocaleString()} / {batch.total_selected.toLocaleString()} ({progress}
              %)
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-brand-dark">
            <div
              className="h-full rounded-full bg-brand-orange transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-brand-text-secondary">
            {Object.entries(batch.counts).map(([status, count]) => (
              <span key={status}>
                {statusLabel(status)}:{' '}
                <strong className="font-mono text-brand-text-primary">{count}</strong>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-brand-text-secondary">
            <span>
              Measured: <strong>{formatBytes(batch.measured_bytes ?? 0)}</strong>
            </span>
            <span>
              Submitted: <strong>{formatBytes(batch.submitted_bytes ?? 0)}</strong>
            </span>
            <span>
              Last minute:{' '}
              <strong>
                {(batch.last_run_submitted_count ?? 0).toLocaleString()} episodes ·{' '}
                {formatBytes(batch.last_run_submitted_bytes ?? 0)} ·{' '}
                {formatBandwidth(batch.last_run_estimated_mbps ?? 0)}
              </strong>
            </span>
          </div>
        </div>
      )}

      {tasks.length > 0 && (
        <div className="max-h-72 overflow-auto border border-brand-border">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-brand-dark text-brand-text-secondary">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Episode ID</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Attempts</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border text-brand-text-primary">
              {tasks.map((task) => (
                <tr key={task.episode_id}>
                  <td className="px-3 py-2 font-mono text-brand-text-secondary">
                    {task.batch_position}
                  </td>
                  <td className="px-3 py-2 font-mono">{task.episode_id}</td>
                  <td className="px-3 py-2 capitalize">{statusLabel(task.status)}</td>
                  <td className="px-3 py-2 font-mono">{task.attempt_count}</td>
                  <td
                    className="max-w-72 truncate px-3 py-2 text-red-400"
                    title={task.last_error ?? ''}
                  >
                    {task.last_error ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CsvExportProgress state={exportState} />
    </section>
  );
}
